import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';
import {
  BenchmarkMySubmissionEntry,
  CatalogueApiService,
  extractBenchmarkMySubmissionsList,
  LeadershipApiService,
} from '@angular-workspace/services';
import { HACKATHON_DATASETS } from '../../../catalogue/reference-datasets/hackathon-datasets.data';

interface DashboardDatasetOption {
  id: string;
  title: string;
}

@Component({
  selector: 'app-dataset-provider-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dataset-provider-dashboard.component.html',
  styleUrl: './dataset-provider-dashboard.component.scss',
})
export class DatasetProviderDashboardComponent implements OnInit {
  datasetOptions: DashboardDatasetOption[] = [];

  loading = true;
  error: string | null = null;
  selectedDatasetId = '';

  modelsSubmittedCount = 0;
  successfulModelInferencesCount = 0;
  highestScore: number | null = null;

  private keycloak = inject(KeycloakService);
  private leadershipApi = inject(LeadershipApiService);
  private catalogueApi = inject(CatalogueApiService);
  private cdr = inject(ChangeDetectorRef);
  private loadRequestId = 0;

  ngOnInit(): void {
    void this.initializeDashboard();
  }

  get selectedDatasetTitle(): string {
    return (
      this.datasetOptions.find((dataset) => dataset.id === this.selectedDatasetId)?.title ??
      'Benchmarking dataset'
    );
  }

  onDatasetChange(datasetId: string): void {
    if (!datasetId || this.selectedDatasetId === datasetId || this.loading) {
      return;
    }
    this.selectedDatasetId = datasetId;
    void this.loadDashboard();
  }

  async initializeDashboard(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    try {
      await this.loadDatasetOptions();
      if (!this.selectedDatasetId) {
        this.error = 'No benchmarking datasets are available yet.';
        this.resetStats();
        return;
      }
      await this.loadDashboard();
    } catch (err) {
      console.error('Error initializing dataset provider dashboard:', err);
      this.error = 'Unable to load submission analytics. Please try again.';
      this.resetStats();
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async loadDashboard(): Promise<void> {
    if (!this.selectedDatasetId) {
      this.loading = false;
      this.error = 'No benchmarking datasets are available yet.';
      this.resetStats();
      this.cdr.markForCheck();
      return;
    }

    const requestId = ++this.loadRequestId;
    this.loading = true;
    this.error = null;
    this.cdr.markForCheck();

    try {
      await this.loadSubmissionStats(requestId);
    } catch (err) {
      if (requestId !== this.loadRequestId) {
        return;
      }
      console.error('Error loading dataset provider dashboard:', err);
      this.error = 'Unable to load submission analytics. Please try again.';
      this.resetStats();
    } finally {
      if (requestId === this.loadRequestId) {
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  formatCount(value: number): string {
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return String(value);
  }

  formatScore(value: number): string {
    if (!Number.isFinite(value)) {
      return '—';
    }
    return value <= 1 ? value.toFixed(2) : value.toLocaleString();
  }

  private resetStats(): void {
    this.modelsSubmittedCount = 0;
    this.successfulModelInferencesCount = 0;
    this.highestScore = null;
  }

  private async loadDatasetOptions(): Promise<void> {
    const fromStatic = HACKATHON_DATASETS.map((dataset) => ({
      id: dataset.leaderboardDatasetId,
      title: dataset.title,
    })).filter((dataset) => !!dataset.id);

    try {
      const response = await this.catalogueApi.searchAllAssets(
        '',
        ['Dataset'],
        [],
        1,
        50,
        'name:asc;itemCreatedAt:asc;',
      );
      const items = this.extractCatalogueItems(response);
      const fromCatalogue = items
        .map((item) => {
          const id = String(item['id'] ?? '').trim();
          if (!id) {
            return null;
          }
          return {
            id,
            title: String(item['label'] ?? item['name'] ?? item['title'] ?? id),
          };
        })
        .filter((item): item is DashboardDatasetOption => !!item);

      this.datasetOptions = fromCatalogue.length > 0 ? fromCatalogue : fromStatic;
    } catch (error) {
      console.warn('Dataset provider dashboard: failed to load catalogue datasets:', error);
      this.datasetOptions = fromStatic;
    }

    if (!this.selectedDatasetId || !this.datasetOptions.some((ds) => ds.id === this.selectedDatasetId)) {
      this.selectedDatasetId = this.datasetOptions[0]?.id ?? '';
    }
  }

  private extractCatalogueItems(response: unknown): Record<string, unknown>[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response.filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
      );
    }

    const record = response as Record<string, unknown>;
    const result = record['result'];
    if (Array.isArray(result)) {
      return result.filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
      );
    }
    if (result && typeof result === 'object') {
      const nested = result as Record<string, unknown>;
      if (Array.isArray(nested['results'])) {
        return nested['results'].filter(
          (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
        );
      }
      if (Array.isArray(nested['items'])) {
        return nested['items'].filter(
          (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
        );
      }
    }
    if (Array.isArray(record['results'])) {
      return record['results'].filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
      );
    }
    if (Array.isArray(record['data'])) {
      return record['data'].filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
      );
    }
    if (Array.isArray(record['items'])) {
      return record['items'].filter(
        (item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item),
      );
    }
    return [];
  }

  private async loadSubmissionStats(requestId: number): Promise<void> {
    const accessToken = await this.resolveAccessToken();
    if (requestId !== this.loadRequestId) {
      return;
    }

    if (!accessToken) {
      console.warn('Dataset provider dashboard: no access token available for submissions API');
      this.resetStats();
      return;
    }

    const response = await this.leadershipApi
      .getBenchmarkSubmissions(this.selectedDatasetId, accessToken)
      .catch((err) => {
        console.warn(`Submissions API unavailable for ${this.selectedDatasetId}:`, err);
        return null;
      });

    if (requestId !== this.loadRequestId) {
      return;
    }

    const entries = extractBenchmarkMySubmissionsList(response);
    let successfulInferences = 0;
    let bestScore = Number.NEGATIVE_INFINITY;

    for (const entry of entries) {
      if (this.isFailedSubmission(entry)) {
        continue;
      }

      successfulInferences += 1;
      const score = this.getPrimaryMetricScore(entry);
      if (score !== null && score > bestScore) {
        bestScore = score;
      }
    }

    this.modelsSubmittedCount = entries.length;
    this.successfulModelInferencesCount = successfulInferences;
    this.highestScore = Number.isFinite(bestScore) ? this.roundScore(bestScore) : null;
    this.cdr.markForCheck();
  }

  private async resolveAccessToken(): Promise<string | null> {
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      if (!isLoggedIn) {
        return null;
      }

      const existingToken = this.keycloak.getKeycloakInstance()?.token?.trim();
      if (existingToken) {
        return existingToken;
      }

      try {
        await this.keycloak.updateToken(30);
      } catch (error) {
        console.warn('Dataset provider dashboard: token refresh skipped:', error);
      }

      const refreshedToken = this.keycloak.getKeycloakInstance()?.token?.trim();
      if (refreshedToken) {
        return refreshedToken;
      }

      const token = await this.keycloak.getToken();
      return token?.trim() ? token : null;
    } catch (error) {
      console.error('Dataset provider dashboard: failed to resolve access token:', error);
      return null;
    }
  }

  private isFailedSubmission(entry: BenchmarkMySubmissionEntry): boolean {
    const apiStatus = (entry.status ?? '').toLowerCase();
    return apiStatus === 'failed' || apiStatus === 'error';
  }

  private getPrimaryMetricScore(entry: BenchmarkMySubmissionEntry): number | null {
    const metrics = this.normalizeSubmissionMetrics(entry.metrics);
    if (!metrics) {
      return null;
    }

    const preferredKeys = [
      'accuracy',
      'macro_f2',
      'weighted_f2',
      'macro_f1',
      'sensitivity',
      'qwk',
      'auc',
      'f2',
      'f1',
    ];

    for (const key of preferredKeys) {
      const value = metrics[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
    }

    const firstMetric = Object.values(metrics).find(
      (value) => typeof value === 'number' && Number.isFinite(value),
    );
    return typeof firstMetric === 'number' ? firstMetric : null;
  }

  private normalizeSubmissionMetrics(
    metrics: unknown,
  ): Record<string, number> | undefined {
    if (metrics == null) {
      return undefined;
    }

    let source: unknown = metrics;
    if (typeof source === 'string') {
      try {
        source = JSON.parse(source);
      } catch {
        return undefined;
      }
    }

    if (typeof source !== 'object' || source === null) {
      return undefined;
    }

    const normalized: Record<string, number> = {};
    for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
        normalized[key] = numericValue;
      }
    }

    return Object.keys(normalized).length > 0 ? normalized : undefined;
  }

  private roundScore(value: unknown): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }
    return Math.round(numericValue * 100) / 100;
  }
}
